/**
 * "Attach the job post" affordance on the create wizard's Tailor step.
 *
 * Owns the pick → extract → merge pipeline shared by the native step
 * (CreateCvTailorStep) and the web TailorStep (create.web.tsx): the platform
 * files only render the row and present errors. The extracted text flows into
 * the wizard through the existing `setJobDescription` action — the machine is
 * untouched, so from its point of view the user simply pasted the posting.
 */
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  extractDocumentText,
  isUnreadableResumeFileError,
  MAX_EXTRACT_FILE_BYTES,
  OnboardingApiError,
  type ExtractableFile,
} from '../../../lib/onboarding-api';

/** What POST /api/onboarding/extract-text accepts: pdf, doc/docx, images, txt. */
export const JOB_POST_ATTACHMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

/** `accept` attribute for the web file input. */
export const JOB_POST_ATTACHMENT_ACCEPT =
  JOB_POST_ATTACHMENT_MIME_TYPES.join(',');

/**
 * Empty field: the extracted text becomes the job description. Existing text
 * (typed or a previous extraction): append after a blank line — never clobber
 * what the user wrote.
 */
export function appendExtractedJobText(current: string, extracted: string) {
  const text = extracted.trim();
  if (!text) return current;
  return current.trim() ? `${current.trimEnd()}\n\n${text}` : text;
}

export type PickedJobPostFile = ExtractableFile & { size?: number | null };

export function useJobPostAttachment({
  jobDescription,
  onJobDescriptionChange,
  onError,
}: {
  jobDescription: string;
  onJobDescriptionChange: (value: string) => void;
  /** Platform-owned presentation: RN Alert.alert natively, alert.web on web. */
  onError: (title: string, message: string) => void;
}) {
  const { t } = useTranslation('seeker');
  const [fileName, setFileName] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  // The extract round-trip takes seconds; read the field at completion time so
  // anything typed during the upload is appended to, not overwritten.
  const jobDescriptionRef = useRef(jobDescription);
  jobDescriptionRef.current = jobDescription;

  const attach = useCallback(
    async (picked: PickedJobPostFile) => {
      if (isExtracting) return;
      if (
        typeof picked.size === 'number' &&
        picked.size > MAX_EXTRACT_FILE_BYTES
      ) {
        onError(
          t('cv.create.tailor.jdAttachFailedTitle'),
          t('cv.create.tailor.jdAttachTooLarge'),
        );
        return;
      }
      setFileName(picked.name);
      setIsExtracting(true);
      try {
        const { text } = await extractDocumentText(picked);
        onJobDescriptionChange(
          appendExtractedJobText(jobDescriptionRef.current, text),
        );
      } catch (error) {
        setFileName(null);
        const message =
          error instanceof OnboardingApiError && error.code === 'FILE_TOO_LARGE'
            ? t('cv.create.tailor.jdAttachTooLarge')
            : isUnreadableResumeFileError(error)
              ? t('cv.create.tailor.jdAttachUnreadable')
              : error instanceof Error && error.message
                ? error.message
                : t('cv.create.tailor.jdAttachUnreadable');
        onError(t('cv.create.tailor.jdAttachFailedTitle'), message);
      } finally {
        setIsExtracting(false);
      }
    },
    [isExtracting, onError, onJobDescriptionChange, t],
  );

  /** Clears only the attachment chip; the extracted text stays editable. */
  const clear = useCallback(() => setFileName(null), []);

  return { fileName, isExtracting, attach, clear };
}
